/**
 * Subject Management AI Button
 * Floating action button to open the Subject Management AI Assistant
 * Reuses the Reports AI chatbot UI components for consistency
 */

import React, { useState, useCallback } from 'react';
import { Tooltip } from 'antd';
import ReportsChatbot from '../../../reports/components/reports-chatbot/reports-chatbot';
import ApiSubjectManagementAi from '../../../../api/subjectManagementAi';
import { useSubjectManagementProgressTracking } from '../../../../hooks/useSubjectManagementProgressTracking';
import './subject-management-ai-button.css';

const SubjectManagementAiButton = ({
  userId,
  subjectContext
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  const handleClick = useCallback(() => {
    setIsOpen(prev => !prev);
    setHasInteracted(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const { currentRoute, projectId, activeFilters, visibleDataSummary } = subjectContext || {};

  // Generate context-aware welcome message
  const welcomeMessage = visibleDataSummary?.subjectId
    ? `Hello! I'm your Subject Management AI Assistant.

I can help you analyze **${visibleDataSummary.displayId}**${visibleDataSummary.firstName ? ` (${visibleDataSummary.firstName} ${visibleDataSummary.lastName})` : ''}:

**All Subject Data Available:**
${visibleDataSummary.tasksLoaded ? `✓ **Tasks** - ${visibleDataSummary.tasksCount} total (${visibleDataSummary.tasksSummary.completed} completed, ${visibleDataSummary.tasksSummary.pending} pending)` : ''}
${visibleDataSummary.analyticsLoaded ? `✓ **Analytics** - Completion & adherence rates loaded` : ''}
${visibleDataSummary.surveyLoaded && visibleDataSummary.onboardingData ? `✓ **Onboarding** - ${visibleDataSummary.onboardingData.completed}/${visibleDataSummary.onboardingData.total} surveys completed (${Math.round(visibleDataSummary.onboardingData.completionRate * 100)}% rate)` : ''}
${visibleDataSummary.enrollmentData?.length > 0 ? `✓ **Enrollment** - ${visibleDataSummary.enrollmentData.length} fields available` : ''}

${visibleDataSummary.currentTab ? `📍 **Currently viewing: ${visibleDataSummary.currentTab.charAt(0).toUpperCase() + visibleDataSummary.currentTab.slice(1)} tab**` : ''}

**I can answer questions about:**
- Task completion, adherence, and patterns
- Onboarding survey responses
- Profile and enrollment data
- Engagement and activity history
- Scenario progress

Ask me anything about this subject!`
    : `Hello! I'm your Subject Management AI Assistant. I can help you analyze:

- **Subject Data** - Active subjects, cohorts, enrollment status
- **Task Analytics** - Adherence rates, completion statistics
- **Engagement Metrics** - Login patterns, activity scores
- **Scenario Progress** - Completion rates and responses

Ask me anything about your subjects!`;

  return (
    <>
      {/* Floating Button */}
      <Tooltip
        title="Subject Management AI Assistant"
        placement="left"
      >
        <button
          className={`subject-management-ai-button ${isOpen ? 'active' : ''} ${!hasInteracted ? 'pulse' : ''}`}
          onClick={handleClick}
          aria-label="Open Subject Management AI Assistant"
        >
          {isOpen ? (
            <span className="subject-management-ai-button-icon" style={{ fontSize: '28px' }}>×</span>
          ) : (
            <svg
              className="subject-management-ai-button-icon"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              style={{ width: '32px', height: '32px' }}
            >
              <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z"/>
              <path d="M19 3L19.5 5L21.5 5.5L19.5 6L19 8L18.5 6L16.5 5.5L18.5 5L19 3Z"/>
              <path d="M5 16L5.5 18L7.5 18.5L5.5 19L5 21L4.5 19L2.5 18.5L4.5 18L5 16Z"/>
            </svg>
          )}
        </button>
      </Tooltip>

      {/* Chatbot Modal - Reusing Reports AI UI */}
      {isOpen && (
        <ReportsChatbot
          userId={userId}
          currentRoute={currentRoute}
          activeFilters={activeFilters || {}}
          visibleDataSummary={visibleDataSummary || null}
          projectId={projectId || null}
          siteIds={null}
          onClose={handleClose}
          apiClient={ApiSubjectManagementAi}
          progressTrackingHook={useSubjectManagementProgressTracking}
          title="Subjects AI"
          welcomeMessage={welcomeMessage}
          localStoragePrefix="subjectManagementChatbot"
        />
      )}
    </>
  );
};

export default SubjectManagementAiButton;
